import React, { useState, useEffect, useMemo } from 'react';
import { db } from './firebase'; 
import { collection, onSnapshot, query, doc, getDoc, setDoc, deleteDoc, updateDoc, serverTimestamp, addDoc } from 'firebase/firestore';
import { FaPlus, FaSearch, FaFilePdf, FaTrashAlt, FaUndo } from 'react-icons/fa';
import Modal from 'react-modal';
import toast, { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import ProjectForm from './components/ProjectForm';
import ProjectPDFViewer from './components/ProjectPDFViewer';

// 🔥 ESTADOS ACTUALIZADOS PARA EVENTOS KOKIMIKO 🔥
const STATUS_OPTIONS = ['Aprobado', 'En Planificación', 'En Producción', 'Día del Evento', 'Pendiente', 'Completado', 'Cancelado', 'Retrasado'];

const ProjectCard = ({ proyecto, vista, onEdit, onViewPdf, onDelete, onRestore, onStatusChange, onPermanentDelete }) => {
    const handleCardClick = (e) => {
        const interactiveTags = ['BUTTON', 'SELECT', 'OPTION', 'svg', 'path'];
        if (interactiveTags.includes(e.target.tagName) || e.target.closest('button')) return;
        onEdit(proyecto);
    };

    const cardVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 },
        exit: { y: -20, opacity: 0 }
    };

    return (
        <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            layout
            whileHover={{ scale: 1.02, zIndex: 10, transition: { duration: 0.2 } }}
            whileTap={{ scale: 0.98 }}
            className="bg-white p-5 border-2 border-gray-100 rounded-[2rem] shadow-sm flex flex-col justify-between min-w-0 transition-colors hover:border-kokimiko-teal/30 group"
        >
            <div className="flex justify-between items-start mb-4 cursor-pointer" onClick={handleCardClick}>
                <div className="flex-grow min-w-0 pr-2">
                    <p className="font-black text-lg text-kokimiko-texto truncate" title={proyecto.nombreProyecto}>{proyecto.nombreProyecto}</p>
                    <p className="text-sm font-bold text-gray-500 truncate uppercase tracking-wider" title={proyecto.nombreCliente}>{proyecto.nombreCliente || 'Familia no asignada'}</p>
                    <p className="text-[10px] text-gray-400 mt-2 uppercase tracking-widest font-bold">Creado: {proyecto.fecha}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <motion.button whileTap={{ scale: 0.9 }} onClick={() => onViewPdf(proyecto)} className="p-2 bg-gray-50 text-gray-400 hover:text-kokimiko-texto hover:bg-gray-100 rounded-xl transition-colors" title="Ver PDF">
                        <FaFilePdf size={16} />
                    </motion.button>
                    {vista === 'eliminados' ? (
                        <>
                            <motion.button whileTap={{ scale: 0.9 }} onClick={() => onRestore(proyecto.id)} className="p-2 bg-green-50 text-green-500 hover:bg-green-500 hover:text-white rounded-xl transition-colors" title="Restaurar Evento">
                                <FaUndo size={14} />
                            </motion.button>
                            <motion.button whileTap={{ scale: 0.9 }} onClick={() => onPermanentDelete(proyecto.id)} className="p-2 bg-red-50 text-red-500 hover:bg-red-500 hover:text-white rounded-xl transition-colors" title="Eliminar para siempre">
                                <FaTrashAlt size={14} />
                            </motion.button>
                        </>
                    ) : (
                        <motion.button whileTap={{ scale: 0.9 }} onClick={() => onDelete(proyecto.id)} className="p-2 bg-red-50 text-kokimiko-naranja hover:bg-kokimiko-naranja hover:text-white rounded-xl transition-colors" title="Cancelar Evento">
                            <FaTrashAlt size={14} />
                        </motion.button>
                    )}
                </div>
            </div>
            <div className="border-t border-gray-100 pt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div className="w-full sm:w-auto bg-gray-50 px-3 py-1 rounded-xl border border-gray-200">
                    <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mr-2">Estado:</label>
                    <select
                        value={proyecto.estado || 'Pendiente'}
                        onChange={(e) => onStatusChange(proyecto, e.target.value)}
                        className={`text-xs font-black p-1 border-0 rounded focus:ring-0 bg-transparent uppercase tracking-wider ${proyecto.estado === 'Completado' ? 'text-kokimiko-verde' : 'text-kokimiko-teal'}`}
                    >
                        {STATUS_OPTIONS.map(status => <option key={status} value={status}>{status}</option>)}
                    </select>
                </div>
                 {vista === 'enTransito' && proyecto.fechaEntrega && (
                    <div className="flex-shrink-0 text-right bg-kokimiko-teal/10 px-3 py-1.5 rounded-xl border border-kokimiko-teal/20">
                         <p className="text-[9px] font-black text-kokimiko-teal uppercase tracking-widest">Fecha Evento:</p>
                         <p className="font-black text-sm text-kokimiko-texto">{proyecto.fechaEntrega}</p>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

const customModalStyles = { content: { top: '50%', left: '50%', right: 'auto', bottom: 'auto', marginRight: '-50%', transform: 'translate(-50%, -50%)', border: 'none', background: 'transparent', width: '90%', maxWidth: '1024px', padding: '0' }, overlay: { backgroundColor: 'rgba(35, 37, 89, 0.75)', zIndex: 1000 }};
const pdfModalStyles = { content: { top: '5%', bottom: '5%', left: '5%', right: '5%', padding: '20px', border: 'none', display: 'flex', flexDirection: 'column', background: '#374151', borderRadius: '2rem' }, overlay: { backgroundColor: 'rgba(35, 37, 89, 0.85)', zIndex: 1000 }};

Modal.setAppElement('#root');

function ProyectosEnTransitoView({ usuario }) {
    const [proyectos, setProyectos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [proyectoActual, setProyectoActual] = useState(null);
    const [logoUrl, setLogoUrl] = useState('');
    const [terminoBusqueda, setTerminoBusqueda] = useState('');
    const [vistaActual, setVistaActual] = useState('enTransito'); 
    const [pdfModalIsOpen, setPdfModalIsOpen] = useState(false);
    const [proyectoParaPdf, setProyectoParaPdf] = useState(null);

    useEffect(() => { 
        if (!usuario) { setCargando(false); return; } 
        let coleccion = ''; 
        if (vistaActual === 'enTransito') coleccion = 'proyectosEnTransito'; 
        else if (vistaActual === 'completados') coleccion = 'proyectosCompletados'; 
        else if (vistaActual === 'eliminados') coleccion = 'proyectosEliminados'; 
        
        if (!coleccion) return; 
        
        setCargando(true); 
        const projectsCollectionRef = collection(db, coleccion); 
        const unsubscribe = onSnapshot(query(projectsCollectionRef), (snapshot) => { 
            const safeFormatDate = (d) => d?.toDate ? d.toDate().toLocaleDateString('es-ES') : 'N/A'; 
            const data = snapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data(), 
                fecha: safeFormatDate(doc.data().fecha), 
                fechaEntrega: safeFormatDate(doc.data().fechaEntrega), 
            })).sort((a, b) => { 
                const dateA = a.fechaEntrega?.split('/').reverse().join('') || '99999999'; 
                const dateB = b.fechaEntrega?.split('/').reverse().join('') || '99999999'; 
                return vistaActual === 'enTransito' ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA); 
            }); 
            setProyectos(data); 
            setCargando(false); 
        }, (error) => { 
            console.error("Error al cargar eventos:", error); 
            toast.error(`Error al cargar la vista: ${vistaActual}`); 
            setCargando(false); 
        }); 
        return () => unsubscribe(); 
    }, [usuario, vistaActual]);

    useEffect(() => { 
        const fetchLogo = async () => { 
            const docSnap = await getDoc(doc(db, 'configuracion', 'empresa')); 
            if (docSnap.exists() && docSnap.data().logoBase64) setLogoUrl(docSnap.data().logoBase64); 
        }; 
        fetchLogo(); 
    }, []);

    const openModal = (proyecto = null) => { setProyectoActual(proyecto); setModalIsOpen(true); };
    const closeModal = () => { setProyectoActual(null); setModalIsOpen(false); };
    const openPdfModal = (proyecto) => { setProyectoParaPdf(proyecto); setPdfModalIsOpen(true); };
    const closePdfModal = () => { setProyectoParaPdf(null); setPdfModalIsOpen(false); };
    
    const moveDocument = async (id, origen, destino, dataToUpdate = {}) => { 
        const origenRef = doc(db, origen, id); 
        const docSnap = await getDoc(origenRef); 
        if (docSnap.exists()) { 
            const finalData = { ...docSnap.data(), ...dataToUpdate }; 
            await setDoc(doc(db, destino, id), finalData); 
            await deleteDoc(origenRef); 
            return true; 
        } 
        return false; 
    };

    const handleSaveProyecto = async (formData) => { 
        try { 
            if (proyectoActual) { 
                const coleccionActual = `proyectos${vistaActual.charAt(0).toUpperCase() + vistaActual.slice(1)}`; 
                const seMueveACompletados = vistaActual === 'enTransito' && formData.estado === 'Completado'; 
                const seMueveAEnTransito = vistaActual === 'completados' && formData.estado !== 'Completado'; 
                
                if (seMueveACompletados) { 
                    await toast.promise( moveDocument(proyectoActual.id, 'proyectosEnTransito', 'proyectosCompletados', formData), { loading: 'Completando y moviendo...', success: '¡Evento completado!', error: 'Error al mover.' } ); 
                } else if (seMueveAEnTransito) { 
                    await toast.promise( moveDocument(proyectoActual.id, 'proyectosCompletados', 'proyectosEnTransito', formData), { loading: 'Reactivando y moviendo...', success: '¡Evento reactivado!', error: 'Error al mover.' } ); 
                } else { 
                    const docRef = doc(db, coleccionActual, proyectoActual.id); 
                    await toast.promise(updateDoc(docRef, { ...formData, updatedAt: serverTimestamp() }), { loading: 'Actualizando...', success: '¡Evento actualizado!', error: 'Error al actualizar.' }); 
                } 
            } else { 
                await toast.promise(addDoc(collection(db, "proyectosEnTransito"), { ...formData, fecha: serverTimestamp() }), { loading: 'Guardando...', success: '¡Nuevo evento agendado!', error: 'Error al guardar.' }); 
            } 
            closeModal(); 
        } catch (error) { 
            console.error("Error al guardar:", error); 
            toast.error("Ocurrió un error al guardar."); 
        } 
    };

    const handleStatusChange = async (proyecto, nuevoEstado) => { 
        const coleccionOrigen = `proyectos${vistaActual.charAt(0).toUpperCase() + vistaActual.slice(1)}`; 
        if (nuevoEstado === 'Completado' && vistaActual === 'enTransito') { 
            if (window.confirm("¿Marcar este evento como completado?")) { 
                await toast.promise( moveDocument(proyecto.id, coleccionOrigen, 'proyectosCompletados', { estado: nuevoEstado }), { loading: 'Completando...', success: 'Evento completado', error: 'Error' } ); 
            } 
        } else if (nuevoEstado !== 'Completado' && vistaActual === 'completados') { 
            if (window.confirm("¿Reactivar evento y mover a 'Próximos Eventos'?")) { 
                await toast.promise( moveDocument(proyecto.id, coleccionOrigen, 'proyectosEnTransito', { estado: nuevoEstado }), { loading: 'Reactivando...', success: 'Evento movido a Próximos', error: 'Error' } ); 
            } 
        } else { 
            await toast.promise(updateDoc(doc(db, coleccionOrigen, proyecto.id), { estado: nuevoEstado }), { loading: 'Actualizando...', success: `Estado actualizado`, error: 'Error' }); 
        } 
    };

    const handleDeleteProyecto = async (proyectoId) => { 
        if (window.confirm("¿Cancelar este evento y mover a la papelera?")) { 
            const origen = `proyectos${vistaActual.charAt(0).toUpperCase() + vistaActual.slice(1)}`; 
            await toast.promise(moveDocument(proyectoId, origen, 'proyectosEliminados'), { loading: 'Cancelando...', success: 'Evento cancelado', error: 'Error' }); 
        } 
    };

    const handleRestoreProyecto = async (proyectoId) => { 
        if (window.confirm("¿Restaurar evento cancelado?")) { 
            await toast.promise(moveDocument(proyectoId, 'proyectosEliminados', 'proyectosEnTransito'), { loading: 'Restaurando...', success: 'Evento restaurado', error: 'Error' }); 
        } 
    };

    const handlePermanentDeleteProyecto = async (proyectoId) => { 
        if (window.confirm("¿ESTÁS SEGURO? Esta acción borrará el evento para siempre.")) { 
            await toast.promise(deleteDoc(doc(db, 'proyectosEliminados', proyectoId)), { loading: 'Eliminando permanentemente...', success: '¡Evento eliminado para siempre!', error: 'Error.' }); 
        } 
    };

    const proyectosFiltrados = useMemo(() => { 
        return proyectos.filter(p => 
            (p.nombreProyecto || '').toLowerCase().includes(terminoBusqueda.toLowerCase()) || 
            (p.nombreCliente || '').toLowerCase().includes(terminoBusqueda.toLowerCase())
        ); 
    }, [proyectos, terminoBusqueda]);


    if (cargando) { return <div className="text-center py-12 text-2xl font-black text-gray-400 animate-pulse uppercase tracking-widest">Cargando Eventos...</div>; }

    const TabButton = ({ id, label }) => (
        <button 
            onClick={() => setVistaActual(id)} 
            className={`py-3 px-6 font-black text-xs uppercase tracking-widest transition-all rounded-t-2xl ${vistaActual === id ? 'bg-white text-kokimiko-texto border-t-4 border-kokimiko-teal shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]' : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'}`}
        >
            {label}
        </button>
    );

    return (
        <div className="h-full flex flex-col p-4 md:p-8 bg-kokimiko-fondo/30 font-sans">
            <Toaster position="top-center" />
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 gap-4">
                <h1 className="text-2xl md:text-3xl font-black text-kokimiko-texto uppercase tracking-widest">
                    Eventos en Producción
                </h1>
                <div className="w-full md:w-auto flex flex-col md:flex-row items-center gap-4">
                    <div className="relative w-full md:w-64">
                        <input type="text" placeholder="Buscar evento o familia..." className="w-full pl-10 pr-4 h-11 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-kokimiko-teal focus:outline-none text-sm font-bold text-kokimiko-texto" value={terminoBusqueda} onChange={e => setTerminoBusqueda(e.target.value)} />
                        <FaSearch className="absolute left-4 top-3.5 text-gray-400" />
                    </div>
                    {usuario?.role === 'admin' && (
                        <motion.button whileTap={{ scale: 0.95 }} onClick={() => openModal()} className="bg-kokimiko-teal text-white font-black h-11 px-4 sm:px-6 rounded-xl shadow-md hover:opacity-80 flex items-center justify-center gap-2 w-full md:w-auto uppercase tracking-widest text-xs">
                            <FaPlus/> <span className="hidden sm:inline">Nuevo Evento</span>
                        </motion.button>
                    )}
                </div>
            </div>
            
            <div className="flex overflow-x-auto custom-scrollbar border-b border-gray-200">
                <TabButton id="enTransito" label="Próximos Eventos" />
                <TabButton id="completados" label="Realizados" />
                <TabButton id="eliminados" label="Cancelados" />
            </div>
            
            <motion.div layout className="flex-grow overflow-y-auto bg-white p-6 rounded-b-[2rem] rounded-tr-[2rem] shadow-sm border border-gray-100 min-h-0">
                <AnimatePresence>
                    {proyectosFiltrados.length > 0 ? (
                        <motion.div 
                            layout
                            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
                        >
                            {proyectosFiltrados.map(proyecto => (
                                <ProjectCard
                                    key={proyecto.id} 
                                    proyecto={proyecto}
                                    vista={vistaActual}
                                    onEdit={openModal}
                                    onDelete={handleDeleteProyecto}
                                    onRestore={handleRestoreProyecto}
                                    onStatusChange={handleStatusChange}
                                    onViewPdf={openPdfModal}
                                    onPermanentDelete={handlePermanentDeleteProyecto}
                                />
                            ))}
                        </motion.div>
                    ) : (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20 text-gray-400 font-bold uppercase tracking-widest border-2 border-dashed border-gray-200 rounded-[2rem]">
                            No hay eventos en esta vista.
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

            {/* Modal principal con animación */}
            <AnimatePresence>
                {modalIsOpen && (
                    <Modal isOpen={true} onRequestClose={closeModal} style={customModalStyles}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                        >
                            {/* IMPORTANTE: Tu componente ProjectForm seguirá funcionando intacto, 
                                solo le pasaremos los datos del evento actual */}
                            <ProjectForm onSave={handleSaveProyecto} onCancel={closeModal} initialData={proyectoActual} usuario={usuario} vistaActual={vistaActual} />
                        </motion.div>
                    </Modal>
                )}
            </AnimatePresence>
            
            {/* Modal de PDF */}
            <Modal isOpen={pdfModalIsOpen} onRequestClose={closePdfModal} style={pdfModalStyles}>
                 <button onClick={closePdfModal} className="absolute top-4 right-4 bg-white border-2 border-kokimiko-texto text-kokimiko-texto rounded-full w-10 h-10 flex items-center justify-center font-black hover:bg-kokimiko-texto hover:text-white transition-colors z-10 shadow-md">X</button>
                 {proyectoParaPdf && <ProjectPDFViewer proyecto={proyectoParaPdf} logoUrl={logoUrl} />}
            </Modal>
        </div>
    );
}

export default ProyectosEnTransitoView;